import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, Shield, FileText, Scale, Users, AlertTriangle, Mail, Building2 } from 'lucide-react';

const TermsPage = () => {
  const lastUpdated = "December 13, 2024";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">innovatorfoundervisas.com</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 px-4 py-2 rounded-full text-sm mb-6">
            <FileText className="w-4 h-4" />
            Legal Document
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms & Conditions
          </h1>
          <p className="text-slate-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-8 md:p-12 space-y-8">
          
          {/* Company Information */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Building2 className="w-6 h-6 text-red-500" />
              Company Information
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                <strong className="text-white">innovatorfoundervisas.com</strong> is a product of <strong className="text-white">Cloud Lab Technologies - UK</strong>, a UK-based IT Consulting Firm.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Company Name</p>
                  <p className="text-white font-semibold">Cloud Lab Technologies - UK</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Location</p>
                  <p className="text-white font-semibold">United Kingdom</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Founder & Owner</p>
                  <p className="text-white font-semibold">Ahmad Raza</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Credentials</p>
                  <p className="text-white font-semibold">UK Innovator Founder Visa Holder & Platform Mentor</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-4">
                Ahmad Raza, the founder of Cloud Lab Technologies - UK, is himself a successful UK Innovator Founder Visa holder. He serves as a mentor on this platform, sharing his first-hand experience and expertise to help aspiring entrepreneurs navigate the UK visa process.
              </p>
            </div>
          </section>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-red-500" />
              1. Introduction
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Welcome to innovatorfoundervisas.com, a product of Cloud Lab Technologies - UK (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;). These Terms and Conditions govern your use of our website and services, including our AI-powered UK Innovator Founder Visa preparation platform.
              </p>
              <p>
                By accessing or using our services, you agree to be bound by these Terms. If you disagree with any part of these terms, you may not access our services.
              </p>
              <p>
                Our platform provides educational tools, AI-assisted business plan generation, mock interview preparation, and informational resources related to the UK Innovator Founder Visa. We are NOT a law firm, immigration consultancy, or endorsing body.
              </p>
            </div>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <FileText className="w-6 h-6 text-red-500" />
              2. Definitions
            </h2>
            <div className="text-slate-300 space-y-3">
              <p><strong className="text-white">&ldquo;Company&rdquo;</strong> refers to Cloud Lab Technologies - UK, the owner and operator of innovatorfoundervisas.com.</p>
              <p><strong className="text-white">&ldquo;Platform&rdquo;</strong> refers to the innovatorfoundervisas.com website and all associated services.</p>
              <p><strong className="text-white">&ldquo;User&rdquo;</strong> refers to any individual or entity accessing or using our Platform.</p>
              <p><strong className="text-white">&ldquo;Services&rdquo;</strong> includes all features such as AI business plan builder, mock interview system, VC discovery engine, proposal templates, and educational content.</p>
              <p><strong className="text-white">&ldquo;Content&rdquo;</strong> means all text, data, graphics, images, audio, video, and other materials generated or provided through our Platform.</p>
              <p><strong className="text-white">&ldquo;AI Credits&rdquo;</strong> refers to the consumable units required to use certain AI-powered features.</p>
              <p><strong className="text-white">&ldquo;Mentor&rdquo;</strong> refers to Ahmad Raza, the platform founder and UK Innovator Founder Visa holder, who provides guidance through the platform.</p>
            </div>
          </section>

          {/* Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-red-500" />
              3. Eligibility & Account Registration
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>To use our Services, you must:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into binding agreements</li>
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Not use the Platform for any illegal or unauthorized purpose</li>
              </ul>
              <p>
                You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use of your account.
              </p>
            </div>
          </section>

          {/* Services Description */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Globe className="w-6 h-6 text-red-500" />
              4. Description of Services
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK, through innovatorfoundervisas.com, provides the following services:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">AI Business Plan Builder:</strong> Automated generation of business plans tailored for UK Innovator Founder Visa applications</li>
                <li><strong className="text-white">Mock Interview System:</strong> AI-powered practice interviews simulating endorsement body assessments</li>
                <li><strong className="text-white">VC Discovery Engine:</strong> Research tools to identify potential investors and market opportunities</li>
                <li><strong className="text-white">Proposal Templates:</strong> Document templates for visa applications and business proposals</li>
                <li><strong className="text-white">Educational Resources:</strong> Guides, tutorials, and information about the UK visa process</li>
                <li><strong className="text-white">Mentorship:</strong> Guidance from Ahmad Raza, a successful UK Innovator Founder Visa holder</li>
              </ul>
            </div>
          </section>

          {/* Important Disclaimer */}
          <section className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              5. Important Disclaimer
            </h2>
            <div className="text-slate-300 space-y-4">
              <p className="font-semibold text-amber-400">
                PLEASE READ THIS SECTION CAREFULLY:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Cloud Lab Technologies - UK is an IT Consulting Firm, NOT a UK Home Office endorsed body</li>
                <li>We are NOT an immigration law firm or regulated immigration adviser</li>
                <li>We do NOT guarantee visa approval or endorsement</li>
                <li>Our services are educational and preparatory tools only</li>
                <li>AI-generated content should be reviewed and verified before submission</li>
                <li>Success in obtaining a visa depends on many factors outside our control</li>
                <li>Ahmad Raza&apos;s role as mentor is to share his personal experience, not to provide legal advice</li>
              </ul>
              <p>
                For official immigration advice, please consult a regulated immigration adviser or solicitor registered with the Office of the Immigration Services Commissioner (OISC) or the Solicitors Regulation Authority (SRA).
              </p>
            </div>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Scale className="w-6 h-6 text-red-500" />
              6. Payment Terms
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>By purchasing any package or credits on our Platform:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You agree to pay all fees and charges associated with your purchase</li>
                <li>Payments are processed through secure third-party payment providers (Stripe, PayPal, etc.)</li>
                <li>All prices are displayed in the currency specified at checkout</li>
                <li>Packages and credits are non-transferable between accounts</li>
                <li>We reserve the right to modify pricing with reasonable notice</li>
                <li>All payments are made to Cloud Lab Technologies - UK</li>
              </ul>
              <p>
                For our refund policy, please refer to our <Link to="/refund-policy" className="text-red-400 hover:text-red-300 underline">Refund Policy</Link> page. Please note that all sales are final once you have logged into the portal.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Intellectual Property</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                All content on our Platform, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, and software, is the property of Cloud Lab Technologies - UK or its content suppliers and is protected by international copyright laws.
              </p>
              <p>
                <strong className="text-white">Your Content:</strong> You retain ownership of any content you create using our tools. However, you grant us a non-exclusive, royalty-free license to use, display, and analyze your content for the purpose of providing and improving our services.
              </p>
              <p>
                <strong className="text-white">AI-Generated Content:</strong> Business plans, interview responses, and other AI-generated content created through our Platform may be used by you for your personal and commercial purposes related to your visa application.
              </p>
            </div>
          </section>

          {/* User Conduct */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Prohibited Conduct</h2>
            <div className="text-slate-300 space-y-4">
              <p>You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use our Platform for any fraudulent or illegal purpose</li>
                <li>Submit false or misleading information</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Platform&apos;s functionality</li>
                <li>Resell, redistribute, or commercialize our services without permission</li>
                <li>Use automated systems or bots to access our Platform</li>
                <li>Impersonate any person or entity</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLOUD LAB TECHNOLOGIES - UK SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
                <li>Visa application rejections or endorsement failures</li>
                <li>Decisions made by UK Home Office, endorsing bodies, or other authorities</li>
                <li>Accuracy of AI-generated content</li>
                <li>Third-party actions or services</li>
              </ul>
              <p>
                Our total liability shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
              </p>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Termination</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                We may terminate or suspend your account and access to our Services immediately, without prior notice, for any reason, including if you breach these Terms.
              </p>
              <p>
                Upon termination, your right to use the Services will cease immediately. Provisions that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Governing Law</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
              </p>
              <p>
                Any disputes arising from these Terms or your use of our Services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
              <p>
                Cloud Lab Technologies - UK is registered and operates in the United Kingdom.
              </p>
            </div>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to Terms</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the &ldquo;Last updated&rdquo; date.
              </p>
              <p>
                Your continued use of the Platform after any changes constitutes acceptance of the new Terms.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-red-500" />
              13. Contact Us
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>If you have any questions about these Terms, please contact us:</p>
              <ul className="space-y-2">
                <li><strong className="text-white">Company:</strong> Cloud Lab Technologies - UK</li>
                <li><strong className="text-white">Email:</strong> legal@innovatorfoundervisas.com</li>
                <li><strong className="text-white">Support:</strong> support@innovatorfoundervisas.com</li>
                <li><strong className="text-white">Website:</strong> innovatorfoundervisas.com</li>
              </ul>
            </div>
          </section>

        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/privacy" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Privacy Policy & GDPR
          </Link>
          <Link 
            to="/refund-policy" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Refund Policy
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 mt-12 py-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Cloud Lab Technologies - UK. All rights reserved.</p>
          <p className="mt-1">innovatorfoundervisas.com is a product of Cloud Lab Technologies - UK</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
